import { AlertCircle, CheckCircle2, MessageSquare, X } from 'lucide-react';

import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { cn } from '../../lib/cn';
import { ReplyForm } from './components/ReplyForm';
import { ThreadTree } from './components/ThreadTree';
import { useListThreadsQuery } from './threads.api';
import type { Thread, ThreadTarget } from './threads.schema';

interface Props {
    target: ThreadTarget | null;
    onClose: () => void;
}

export function ThreadsModal({ target, onClose }: Props) {
    return (
        <Modal open={target !== null} onClose={onClose} ariaLabelledBy="threads-modal-title">
            {target && <ThreadsModalContent target={target} onClose={onClose} />}
        </Modal>
    );
}

function countOpenResolved(threads: Thread[] | undefined): { open: number; resolved: number } {
    if (!threads) return { open: 0, resolved: 0 };
    const resolvedRootIds = new Set<number>();
    for (const t of threads) {
        const r = t.params?.resolvesIssueId;
        if (r != null) resolvedRootIds.add(Number(r));
    }
    let open = 0;
    let resolved = 0;
    for (const t of threads) {
        if (t.parentId != null) continue;
        if (resolvedRootIds.has(t.id)) resolved++;
        else open++;
    }
    return { open, resolved };
}

function ThreadsModalContent({ target, onClose }: { target: ThreadTarget; onClose: () => void }) {
    const { data: threads, isLoading, isError, refetch } = useListThreadsQuery(target);
    const { open, resolved } = countOpenResolved(threads);

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            className={cn(
                'flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/5',
                'bg-white dark:bg-gray-900 dark:ring-white/10',
            )}
        >
            <header className="flex items-start gap-3 border-b border-solid border-gray-100 px-5 py-4 dark:border-gray-800">
                <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300">
                    <MessageSquare aria-hidden="true" className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <h2
                        id="threads-modal-title"
                        className="truncate text-base font-semibold text-gray-900 dark:text-gray-100"
                    >
                        Probleme
                    </h2>
                    {target.label && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{target.label}</p>
                    )}
                    {!isLoading && !isError && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            <StatChip
                                tone="open"
                                count={open}
                                singular="problemă deschisă"
                                plural="probleme deschise"
                            />
                            <StatChip
                                tone="resolved"
                                count={resolved}
                                singular="rezolvată"
                                plural="rezolvate"
                            />
                        </div>
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Închide"
                    onClick={onClose}
                    className="shrink-0"
                >
                    <X aria-hidden="true" className="h-4 w-4" />
                </Button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/40 px-5 py-4 dark:bg-gray-950/40">
                {isLoading && <ThreadsSkeleton />}
                {isError && !isLoading && (
                    <div className="rounded-xl border border-solid border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
                        <p>Eroare la încărcare.</p>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="mt-2"
                            onClick={() => refetch()}
                        >
                            Reîncearcă
                        </Button>
                    </div>
                )}
                {!isLoading && !isError && threads && <ThreadTree threads={threads} target={target} />}
            </div>

            <footer className="border-t border-solid border-gray-100 bg-white px-5 py-4 dark:border-gray-800 dark:bg-gray-900">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Deschide o problemă nouă
                </p>
                <ReplyForm target={target} />
            </footer>
        </div>
    );
}

interface StatChipProps {
    tone: 'open' | 'resolved';
    count: number;
    singular: string;
    plural: string;
}

function StatChip({ tone, count, singular, plural }: StatChipProps) {
    if (count === 0) return null;
    const label = count === 1 ? singular : plural;
    const Icon = tone === 'open' ? AlertCircle : CheckCircle2;
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
                tone === 'open'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
            )}
        >
            <Icon aria-hidden="true" className="h-3 w-3" />
            <strong className="font-semibold">{count}</strong>
            <span>{label}</span>
        </span>
    );
}

function ThreadsSkeleton() {
    return (
        <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className="overflow-hidden rounded-xl border border-solid border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                        <div className="flex-1">
                            <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                            <div className="mt-2 h-2 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
